import { useState, useEffect, useRef, useCallback } from "react";
import { Link } from "react-router-dom";
import { ScrollSection } from "@/components/main-page/ScrollSection";

const AGENT_COLORS = {
  immigration: { dot: "#10b981", bg: "rgba(16, 185, 129, 0.06)", border: "rgb(16 185 129 / 0.3)" },
  education: { dot: "#a78bfa", bg: "rgba(167, 139, 250, 0.06)", border: "rgb(167 139 250 / 0.3)" },
  finserv: { dot: "#f59e0b", bg: "rgba(245, 158, 11, 0.06)", border: "rgb(245 158 11 / 0.3)" },
} as const;

const AGENTS = [
  {
    id: "immigration" as const,
    name: "Immigration",
    status: "Live",
    description:
      "Processes UKVI visa applications end-to-end. Auto-fills forms, cross-checks evidence, flags conflicts. Human QA on every output.",
    stats: "1,200+ cases processed · 98.7% accuracy · 4× faster than manual",
  },
  {
    id: "education" as const,
    name: "Education",
    status: "Launching",
    description:
      "AI learning platform for children. Four-layer safety: input filtering, keyword detection, AI audit, output validation. Portals for students, parents, and schools.",
    stats: "K-12 curriculum · 4-layer safety · 3 portal types",
  },
  {
    id: "finserv" as const,
    name: "Investment",
    status: "Next",
    description:
      "Portfolio analysis, risk scoring, and compliance checks across fund structures. Automates due diligence, generates suitability reports, and flags regulatory breaches before they happen.",
    stats: "Portfolio analysis · Risk scoring · Due diligence · FCA-aligned",
  },
];

const AUDIT_ENTRIES = [
  { time: "14:19", action: "Audit trail exported", status: "Review" as const },
  { time: "14:21", action: "SLA threshold check", status: "Pass" as const },
  { time: "14:23", action: "Evidence cross-check complete", status: "Pass" as const },
  { time: "14:25", action: "Case #1846 form validated", status: "Pass" as const },
  { time: "14:27", action: "Missing supporting doc", status: "Escalate" as const },
  { time: "14:29", action: "Identity check complete", status: "Pass" as const },
  { time: "14:31", action: "Document conflict flagged", status: "Review" as const },
  { time: "14:32", action: "Case #1847 form validated", status: "Pass" as const },
];

const INFRA_ITEMS = ["SSO", "Billing", "Encryption", "Data residency", "Access control", "API layer"];

const STATUS_COLORS = {
  Pass: "text-emerald-500 bg-emerald-500/20",
  Review: "text-amber-500 bg-amber-500/20",
  Escalate: "text-red-500 bg-red-500/20",
} as const;

export function BentoGridSection() {
  const [selected, setSelected] = useState(0);
  const [visibleEntries, setVisibleEntries] = useState(AUDIT_ENTRIES.slice(0, 4));
  const [auditPaused, setAuditPaused] = useState(false);
  const [agentPaused, setAgentPaused] = useState(false);
  const indexRef = useRef(4);

  // Auto-cycle agents every 4s
  useEffect(() => {
    if (agentPaused) return;
    const interval = setInterval(() => {
      setSelected((prev) => (prev + 1) % AGENTS.length);
    }, 4000);
    return () => clearInterval(interval);
  }, [agentPaused]);

  // Auto-cycle audit entries every 2s
  useEffect(() => {
    if (auditPaused) return;
    const interval = setInterval(() => {
      const nextIdx = indexRef.current % AUDIT_ENTRIES.length;
      indexRef.current++;
      const nextEntry = AUDIT_ENTRIES[nextIdx]!;
      setVisibleEntries((prev) => [nextEntry, ...prev.slice(0, 3)]);
    }, 2000);
    return () => clearInterval(interval);
  }, [auditPaused]);

  const handleAgentClick = useCallback((i: number) => {
    setSelected(i);
    setAgentPaused(true);
    // Resume after 8s of inactivity
    setTimeout(() => setAgentPaused(false), 8000);
  }, []);

  return (
    <ScrollSection id="platform" title="One platform. Multiple agents." subtitle="Every COSX agent runs on a shared foundation. New verticals in weeks, not years.">
      <div className="grid md:grid-cols-2 gap-4">
        {/* Agent selector — spans 2 rows on desktop */}
        <div
          className="md:row-span-2 bg-[var(--color-surface)] border border-[var(--color-border)] p-8 flex flex-col"
          onMouseEnter={() => setAgentPaused(true)}
          onMouseLeave={() => setAgentPaused(false)}
        >
          <div className="text-[10px] font-[family-name:var(--font-mono)] tracking-widest uppercase text-[var(--color-fg-tertiary)] mb-6">
            AI agents
          </div>
          <div className="flex flex-col gap-2 mb-6">
            {AGENTS.map((agent, i) => {
              const colors = AGENT_COLORS[agent.id];
              const isSelected = i === selected;
              return (
                <button
                  key={agent.id}
                  onClick={() => handleAgentClick(i)}
                  className="flex items-center gap-3 px-4 py-3 text-left transition-all border-l-[3px]"
                  style={{
                    borderLeftColor: isSelected ? colors.dot : "transparent",
                    background: isSelected ? colors.bg : "var(--color-bg)",
                    borderTop: "1px solid var(--color-border)",
                    borderRight: "1px solid var(--color-border)",
                    borderBottom: "1px solid var(--color-border)",
                  }}
                >
                  <span
                    className="w-2.5 h-2.5 rounded-full flex-shrink-0"
                    style={{ background: colors.dot }}
                  />
                  <span className="text-sm font-medium">{agent.name}</span>
                  <span className="text-[10px] font-[family-name:var(--font-mono)] tracking-widest uppercase text-[var(--color-fg-tertiary)] ml-auto">
                    {agent.status}
                  </span>
                </button>
              );
            })}
          </div>
          <div className="bg-[var(--color-bg)] border border-[var(--color-border)] p-5 flex-1 flex flex-col">
            <div className="flex items-center gap-2 mb-3">
              <span
                className="w-2 h-2 rounded-full flex-shrink-0"
                style={{ background: AGENT_COLORS[AGENTS[selected]!.id].dot }}
              />
              <span className="text-sm font-medium">{AGENTS[selected]?.name}</span>
            </div>
            <p className="text-sm text-[var(--color-fg-secondary)] leading-relaxed mb-4">
              {AGENTS[selected]?.description}
            </p>
            <div className="mt-auto pt-3 border-t border-[var(--color-border)]">
              <span className="text-[11px] font-[family-name:var(--font-mono)] text-[var(--color-fg-tertiary)]">
                {AGENTS[selected]?.stats}
              </span>
            </div>
          </div>
        </div>

        {/* Audit trail — top right */}
        <div
          className="bg-[var(--color-surface)] border border-[var(--color-border)] p-8 overflow-hidden"
          onMouseEnter={() => setAuditPaused(true)}
          onMouseLeave={() => setAuditPaused(false)}
        >
          <div className="text-[10px] font-[family-name:var(--font-mono)] tracking-widest uppercase text-[var(--color-fg-tertiary)] mb-1">
            Governance
          </div>
          <div className="text-sm text-[var(--color-fg-secondary)] mb-6">Live audit trail</div>
          <div className="flex flex-col divide-y divide-[var(--color-border)]">
            {visibleEntries.map((entry, i) => (
              <div
                key={`${entry.time}-${i}`}
                className="flex items-center gap-4 text-sm py-3 first:pt-0 min-w-0"
              >
                <span className="text-[var(--color-fg-tertiary)] font-[family-name:var(--font-mono)] tabular-nums w-12 flex-shrink-0">
                  {entry.time}
                </span>
                <span
                  className={`px-2 py-0.5 rounded text-[10px] font-[family-name:var(--font-mono)] tracking-wider flex-shrink-0 w-[70px] text-center ${STATUS_COLORS[entry.status]}`}
                >
                  {entry.status}
                </span>
                <span className="text-[var(--color-fg-secondary)] truncate flex-1">
                  {entry.action}
                </span>
              </div>
            ))}
          </div>
        </div>

        {/* Infrastructure — bottom right: animated marquee */}
        <div className="bg-[var(--color-surface)] border border-[var(--color-border)] p-8 overflow-hidden">
          <div className="text-[10px] font-[family-name:var(--font-mono)] tracking-widest uppercase text-[var(--color-fg-tertiary)] mb-6">
            Shared infrastructure
          </div>
          <div className="flex flex-col gap-3">
            {/* Row 1 — scrolls left */}
            <div className="relative overflow-hidden">
              <div className="flex gap-3 animate-[marqueeLeft_20s_linear_infinite]">
                {[...INFRA_ITEMS, ...INFRA_ITEMS].map((item, i) => (
                  <span
                    key={`a-${i}`}
                    className="flex-shrink-0 px-4 py-2 border border-[var(--color-border)] bg-[var(--color-bg)] text-xs text-[var(--color-fg-secondary)] whitespace-nowrap"
                  >
                    {item}
                  </span>
                ))}
              </div>
            </div>
            {/* Row 2 — scrolls right */}
            <div className="relative overflow-hidden">
              <div className="flex gap-3 animate-[marqueeRight_25s_linear_infinite]">
                {[...INFRA_ITEMS.slice(3), ...INFRA_ITEMS, ...INFRA_ITEMS.slice(0, 3)].map((item, i) => (
                  <span
                    key={`b-${i}`}
                    className="flex-shrink-0 px-4 py-2 border border-[var(--color-border)] bg-[var(--color-bg)] text-xs text-[var(--color-fg-secondary)] whitespace-nowrap"
                  >
                    {item}
                  </span>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
      <div className="mt-8">
        <Link
          to="/platform"
          className="text-sm font-[family-name:var(--font-mono)] text-[var(--color-fg-secondary)] hover:text-[var(--color-fg)] transition-colors"
        >
          Explore the platform →
        </Link>
      </div>
    </ScrollSection>
  );
}
